/**
 * OnboardingContext
 *
 * Provides onboarding state to the entire authenticated layout:
 *   - onboardingComplete: boolean — has the user finished the first-time wizard?
 *   - agentCount: number — how many agents the user currently owns
 *   - featureTourDismissed: boolean — has the user dismissed the guided tour?
 *   - loading: boolean — still fetching initial state
 *
 * Reads the flag from Firebase RTDB at users/{uid}/onboardingComplete.
 * Reads agent count from GET /api/agents.
 * Caches values so child components don't trigger extra DB reads.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getDatabase, ref, get, set } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import config from '../utils/EnvironmentSwitcher';

// ---------- Context + default shape ----------
const OnboardingContext = createContext({
  onboardingComplete: false,
  setOnboardingComplete: () => {},
  agentCount: 0,
  refreshAgentCount: () => {},
  featureTourDismissed: false,
  setFeatureTourDismissed: () => {},
  loading: true,
});

/**
 * Hook for consuming onboarding state.
 */
export const useOnboarding = () => useContext(OnboardingContext);

/**
 * Provider — wrap inside DefaultLayout (after auth is guaranteed).
 * Expects `uid` prop from the parent that already has authUser.
 */
export const OnboardingProvider = ({ uid, children }) => {
  const [onboardingComplete, setOnboardingCompleteState] = useState(false);
  const [agentCount, setAgentCount] = useState(0);
  const [featureTourDismissed, setFeatureTourDismissedState] = useState(false);
  const [loading, setLoading] = useState(true);

  const getAgentsCountFromResponse = (data) => {
    if (Array.isArray(data)) return data.length;
    if (Array.isArray(data?.agents)) return data.agents.length;
    if (Array.isArray(data?.items)) return data.items.length;
    if (typeof data?.count === 'number') return data.count;
    return 0;
  };

  // ---- Initial load: fetch flags + agent count before deciding to show wizard ----
  // Users with agents skip onboarding even if onboardingComplete is false in Firebase
  useEffect(() => {
    if (!uid) {
      setLoading(false);
      setOnboardingCompleteState(false);
      return;
    }

    const fetchAll = async () => {
      setLoading(true);
      try {
        const db = getDatabase();
        const auth = getAuth();
        let user = auth.currentUser;
        let retries = 0;
        while (!user && retries < 10) {
          await new Promise((r) => setTimeout(r, 250));
          user = auth.currentUser;
          retries += 1;
        }

        const onboardRef = ref(db, `users/${uid}/onboardingComplete`);
        const tourRef = ref(db, `users/${uid}/featureTourDismissed`);
        const agentPromise =
          user
            ? user.getIdToken().then((token) =>
                fetch(`${config.apiUrl}/api/agents`, {
                  headers: { Authorization: `Bearer ${token}` },
                }),
              )
            : Promise.resolve(null);

        const [onboardSnap, tourSnap, agentRes] = await Promise.all([
          get(onboardRef),
          get(tourRef),
          agentPromise,
        ]);

        const isCompleteFromFirebase = onboardSnap.exists() ? onboardSnap.val() === true : false;
        const tourDone = tourSnap.exists() ? tourSnap.val() === true : false;
        setFeatureTourDismissedState(tourDone);

        let agentCountVal = 0;
        if (agentRes?.ok) {
          const data = await agentRes.json();
          agentCountVal = getAgentsCountFromResponse(data);
          setAgentCount(agentCountVal);
        }

        const localAgentsKey = `mie_has_agents_${uid}`;
        const hasAgentsLocal = localStorage.getItem(localAgentsKey) === 'true';
        const effectiveComplete = isCompleteFromFirebase || agentCountVal > 0 || hasAgentsLocal;
        setOnboardingCompleteState(effectiveComplete);

        if (agentCountVal > 0 && !isCompleteFromFirebase) {
          await set(ref(db, `users/${uid}/onboardingComplete`), true);
          localStorage.setItem(localAgentsKey, 'true');
        }
      } catch (err) {
        console.warn('OnboardingContext: failed to fetch', err);
        if (uid && localStorage.getItem(`mie_has_agents_${uid}`) === 'true') {
          setOnboardingCompleteState(true);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [uid]);

  // ---- Fetch agent count from the API (runs in background, does not block loading) ----
  const refreshAgentCount = useCallback(async () => {
    if (!uid) return;
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      const res = await fetch(`${config.apiUrl}/api/agents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const count = getAgentsCountFromResponse(data);
        setAgentCount(count);
        if (count > 0) {
          localStorage.setItem(`mie_has_agents_${uid}`, 'true');
          setOnboardingCompleteState(true);
        }
      }
    } catch (err) {
      console.warn('OnboardingContext: failed to fetch agent count', err);
    }
  }, [uid]);

  // ---- Setters that persist to Firebase RTDB ----

  const setOnboardingComplete = useCallback(
    async (value) => {
      setOnboardingCompleteState(value);
      if (!uid) return;
      try {
        const db = getDatabase();
        await set(ref(db, `users/${uid}/onboardingComplete`), value);
      } catch (err) {
        console.warn('OnboardingContext: failed to write onboardingComplete', err);
      }
    },
    [uid],
  );

  const setFeatureTourDismissed = useCallback(
    async (value) => {
      setFeatureTourDismissedState(value);
      if (!uid) return;
      try {
        const db = getDatabase();
        await set(ref(db, `users/${uid}/featureTourDismissed`), value);
      } catch (err) {
        console.warn('OnboardingContext: failed to write featureTourDismissed', err);
      }
    },
    [uid],
  );

  return (
    <OnboardingContext.Provider
      value={{
        onboardingComplete,
        setOnboardingComplete,
        agentCount,
        refreshAgentCount,
        featureTourDismissed,
        setFeatureTourDismissed,
        loading,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingContext;
